import React from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { designSystem } from '../../design-system';

// Heroセクションのコンテナ
const HeroContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  backgroundColor: 'transparent',
  overflow: 'visible',
  zIndex: 1,
});

// Heroセクション
const HeroSection = styled(Box)({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center',
  zIndex: 1,
  paddingBottom: '120px',

  '@media (max-width: 1440px)': {
    paddingBottom: '100px',
  },

  '@media (max-width: 1024px)': {
    paddingBottom: '80px',
  },

  '@media (max-width: 768px)': {
    paddingBottom: '60px',
  },
});

const HeroContent = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  width: '100%',
  padding: '0 80px',
  height: '100%',

  '@media (max-width: 1600px)': {
    padding: '0 64px',
  },

  '@media (max-width: 1440px)': {
    padding: '0 48px',
  },

  '@media (max-width: 1024px)': {
    flexDirection: 'column',
    gap: '40px',
    alignItems: 'flex-start',
    padding: '0 40px',
  },

  '@media (max-width: 768px)': {
    padding: '0 24px',
  },
});

const TitleImageContainer = styled(Box)({
  width: '806px',
  height: '285px',
  position: 'relative',

  '@media (max-width: 1600px)': {
    width: '700px',
    height: '248px',
  },

  '@media (max-width: 1440px)': {
    width: '600px',
    height: '212px',
  },

  '@media (max-width: 1200px)': {
    width: '550px',
    height: '195px',
  },

  '@media (max-width: 1024px)': {
    width: '100%',
    maxWidth: '600px',
    height: 'auto',
  },

  '@media (max-width: 768px)': {
    maxWidth: '100%',
    height: 'auto',
  },
});

const TitleImage = styled('img')({
  width: '100%',
  height: 'auto',
  objectFit: 'contain',
});

const NewsCard = styled(Box)({
  width: '421px',
  padding: '24px',
  borderRadius: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  backdropFilter: 'blur(25px)',
  WebkitBackdropFilter: 'blur(25px)',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',

  '@media (max-width: 1600px)': {
    width: '380px',
  },

  '@media (max-width: 1440px)': {
    width: '340px',
    padding: '20px',
  },

  '@media (max-width: 1200px)': {
    width: '320px',
  },

  '@media (max-width: 1024px)': {
    width: '100%',
    maxWidth: '400px',
  },

  '@media (max-width: 768px)': {
    maxWidth: '100%',
  },
});

const NewsLabel = styled('p')({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '16px',
  fontWeight: 500,
  letterSpacing: '0.16px',
  background: 'linear-gradient(90deg, #4A9FFF 0%, #00D4FF 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  margin: 0,

  '@media (max-width: 1440px)': {
    fontSize: '14px',
  },
});

const NewsDate = styled('p')({
  fontFamily: designSystem.typography.fontFamily.en,
  fontSize: '16px',
  fontWeight: 400,
  letterSpacing: '0.16px',
  color: '#D0D8E0',
  margin: 0,

  '@media (max-width: 1440px)': {
    fontSize: '14px',
  },
});

const NewsTitle = styled('p')({
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '20px',
  fontWeight: 700,
  letterSpacing: '0.2px',
  color: '#D0D8E0',
  margin: 0,
  lineHeight: 1.5,

  '@media (max-width: 1600px)': {
    fontSize: '18px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '16px',
  },
});

const ScrollIndicator = styled(Box)({
  position: 'absolute',
  bottom: '40px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',

  '& .scroll-text': {
    fontFamily: designSystem.typography.fontFamily.en,
    fontSize: '12px',
    fontWeight: 300,
    color: designSystem.colors.lightText,
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },

  '& .scroll-line': {
    width: '1px',
    height: '40px',
    background: `linear-gradient(to bottom, ${designSystem.colors.lightText}, transparent)`,
    animation: 'scrollAnimation 2s ease-in-out infinite',
  },

  '@keyframes scrollAnimation': {
    '0%': { opacity: 0, transform: 'translateY(-10px)' },
    '50%': { opacity: 1, transform: 'translateY(0)' },
    '100%': { opacity: 0, transform: 'translateY(10px)' },
  },
});

const HeroSectionComponent = () => {
  const [heroRef, heroVisible] = useScrollAnimation({ threshold: 0.3 });

  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const heroVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1, ease: [0.6, 0.01, 0.05, 0.95] },
    },
  };

  return (
    <HeroContainer>
      {/* Heroセクション */}
      <HeroSection ref={heroRef}>
        <HeroContent>
          <motion.div
            variants={heroVariants}
            initial="hidden"
            animate={heroVisible ? 'visible' : 'hidden'}
          >
            <TitleImageContainer>
              <TitleImage src="/title_hero.png" alt="輝くためではなく、照らすために。" />
            </TitleImageContainer>
          </motion.div>

          <motion.div
            variants={heroVariants}
            initial="hidden"
            animate={heroVisible ? 'visible' : 'hidden'}
            transition={{ delay: 0.3 }}
          >
            <NewsCard>
              <NewsLabel>NEWS</NewsLabel>
              <NewsDate>2025.10.20</NewsDate>
              <NewsTitle>コーポレートサイトをリニューアルしました</NewsTitle>
            </NewsCard>
          </motion.div>
        </HeroContent>

        <ScrollIndicator onClick={scrollToAbout}>
          <div className="scroll-text">Scroll</div>
          <div className="scroll-line" />
        </ScrollIndicator>
      </HeroSection>
    </HeroContainer>
  );
};

export default HeroSectionComponent;
